// Shared building blocks for the MAGAZINE skin's Tier-2 layouts (the cover-hero
// Home and the gallery category lists). These render the editorial look — heavy
// Anton type, lime/pop accents, halftone colour-field tiles, skewed sticker
// kickers, frosted glass — and are only used when getSkin() === "magazine", so
// they use the fixed magazine palette inline rather than reading CSS tokens.
import { el } from "../components/dom.js";
import { awardLabel } from "../components/status-badge.js";
import { formatRuntime } from "./format.js";
import { comicFormatShort } from "./comics.js";

export const MAG = {
	lime: "#b6f23d",
	yellow: "#ffd60a",
	ink: "#f5f5f7",
	dim: "#9a9aa2",
	faint: "#83838b",
	bgGlass: "rgba(255,255,255,.06)",
	line: "rgba(255,255,255,.14)",
};

// Per-category colour + label, keyed by the media_type used across the app.
export const MAG_CAT = {
	videogame: { label: "Videogame", color: "#8b3dff", deep: "#5a13a8", count: "Games", verb: "Playing", route: "/games" },
	book: { label: "Book", color: "#ff7a1a", deep: "#7a2e00", count: "Books", verb: "Reading", route: "/books" },
	tv: { label: "TV", color: "#19d3e8", deep: "#0a4a6b", count: "TV", verb: "Watching", route: "/tv" },
	anime: { label: "Anime", color: "#ff3d8b", deep: "#7a0a3d", count: "Anime", verb: "Watching", route: "/anime" },
	movie: { label: "Movie", color: "#e5383b", deep: "#5c0a0f", count: "Movies", verb: "Watching", route: "/movies" },
	comic: { label: "Comic", color: "#1fbf5f", deep: "#0a4a24", count: "Comics", verb: "Reading", route: "/comics" },
};
export const MAG_CAT_ORDER = ["videogame", "book", "tv", "anime", "movie", "comic"];

export function magCat(mediaType) {
	return MAG_CAT[mediaType] || MAG_CAT.videogame;
}

// Cover art across both the normalized (listAllMedia) and the raw per-type
// shapes: books prefer an override; everything else has cover_url.
export function coverOf(item) {
	return item.cover_override_url || item.cover_url || null;
}

// Release/publication year across the per-type field names.
export function yearOf(item) {
	return item.release_year ?? item.published_year ?? item.premiered_year ?? null;
}

// The secondary line of a LIST-view row, per media type: what you'd want under
// the title besides the status + year that the kicker already shows. Games:
// genres; books: authors; TV: nothing catalog-side; anime: demographic + genres;
// movies: runtime · genres; comics: edition type · writer/artist names. The first award is appended for every type. Empty
// string when there's nothing to say.
export function rowSubline(item, mediaType) {
	const parts = [];
	const genres = (item.genres ?? []).slice(0, 3).join(", ");
	if (mediaType === "videogame") {
		parts.push(genres);
	} else if (mediaType === "book") {
		parts.push((item.authors ?? []).join(", "));
	} else if (mediaType === "anime") {
		parts.push([...(item.demographics ?? []), ...(item.genres ?? [])].slice(0, 3).join(", "));
	} else if (mediaType === "movie") {
		parts.push(formatRuntime(item.runtime), genres);
	} else if (mediaType === "comic") {
		parts.push(
			comicFormatShort(item.format),
			(item.creators ?? []).slice(0, 2).map((c) => c.name).join(", "),
		);
	}
	const award = (item.awards ?? [])[0];
	if (award) parts.push(awardLabel(award));
	return parts.filter(Boolean).join(" · ");
}

// A stylized colour-field tile background (halftone dots over a category-tinted
// duotone), or the real cover art if we have it (kept legible under the glass
// overlays per the redesign brief).
export function magTileBg(item) {
	const cat = magCat(item.media_type);
	const cover = coverOf(item);
	if (cover) {
		return `linear-gradient(180deg, rgba(0,0,0,.12), rgba(0,0,0,.58)), url("${cover}") center/cover`;
	}
	return `radial-gradient(rgba(255,255,255,.10) 1.2px, transparent 1.5px) 0 0/9px 9px, linear-gradient(150deg, ${cat.color} 0%, ${cat.deep} 62%, #14041f 100%)`;
}

// A skewed sticker kicker — the magazine signature label.
export function magSticker(text, { bg = MAG.lime, color = "#0b0b0f", size = "9px" } = {}) {
	return el(
		"span",
		{
			style: {
				fontFamily: "'Archivo', sans-serif",
				fontWeight: "900",
				fontSize: size,
				letterSpacing: ".1em",
				textTransform: "uppercase",
				color,
				background: bg,
				padding: "4px 8px",
				transform: "skewX(-7deg)",
				display: "inline-block",
				boxShadow: "0 3px 10px rgba(0,0,0,.3)",
			},
		},
		text,
	);
}

// A sticker positioned absolutely inside a tile.
export function magStickerAbsolute(text, { top, left, right, rotate = "0deg", bg = MAG.lime, color = "#0b0b0f", size = "11px" } = {}) {
	const node = magSticker(text, { bg, color, size });
	Object.assign(node.style, {
		position: "absolute", top, left, right,
		transform: `skewX(-7deg) rotate(${rotate})`,
	});
	return node;
}

// Editorial section heading: a sticker label + a fading rule.
export function magSectionHead(text, accent = MAG.yellow) {
	return el(
		"div",
		{ style: { display: "flex", alignItems: "center", gap: "8px", padding: "20px 4px 10px" } },
		magSticker(text, { bg: accent, size: "11px" }),
		el("span", {
			style: { flex: "1", height: "2px", background: `linear-gradient(90deg, ${accent}99, ${accent}00)` },
		}),
	);
}
